import React, { useState, useEffect } from 'react';
import { Check, Zap, ChevronRight, Monitor, TrendingUp, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { ServiceCard } from './ServiceCard';

const WindowsCard = ({ t, isAuthenticated, isClient, loading, success, windowsStandardBase,
  windowsFeatures, getFlashSaleDiscount, openOrderModal, flashSaleEnd }) => {
  const disc = getFlashSaleDiscount('windows');
  const discounted = disc > 0;
  const finalPrice = discounted ? Math.round(windowsStandardBase * (1 - disc / 100) * 100) / 100 : windowsStandardBase;

  const [remaining, setRemaining] = useState(null);
  useEffect(() => {
    if (!flashSaleEnd) { setRemaining(null); return; }
    const tick = () => {
      const diff = new Date(flashSaleEnd).getTime() - Date.now();
      setRemaining(diff > 0 ? diff : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [flashSaleEnd]);
  const fmtTimer = (ms) => {
    const s = Math.floor(ms / 1000);
    const p = (n) => String(n).padStart(2, '0');
    return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
  };
  const lastHour = remaining !== null && remaining > 0 && remaining <= 3600000;

  if (isClient) return null;

  return (
    <ServiceCard testId="windows-card">
      <div className="group relative h-full rounded-2xl p-6 md:p-8 border border-[rgba(29,233,208,0.15)] bg-gradient-to-b from-zinc-900/80 to-zinc-950/90 overflow-hidden flex flex-col" data-testid="windows-card">
        {discounted && (
          <div data-testid="windows-promo-badge" className="absolute top-4 right-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[10px] md:text-xs font-bold px-2.5 py-1 rounded-full shadow-[0_0_18px_-4px_rgba(16,185,129,0.7)] flex items-center gap-1 z-10 animate-pulse">
            <Zap className="w-3 h-3" />-{disc}%
          </div>
        )}

        {/* En-tête : icône dans carré arrondi + titre */}
        <div className="relative flex items-center gap-3 mb-6">
          <div className="service-card-icon w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border border-[#1a2530] bg-gradient-to-br from-[#0f1620] to-[#0b0f15]">
            <Monitor className="w-5 h-5 text-[#1de9d0]" strokeWidth={2} />
          </div>
          <div>
            <h3 data-testid="windows-title" className="text-lg md:text-xl font-bold text-white leading-tight">{t('services.windows.name')}</h3>
            <p className="text-xs md:text-sm text-gray-500 mt-0.5">{t('services.systemOptimization')}</p>
          </div>
        </div>

        {/* Prix en gros dégradé */}
        <div className="relative mb-4" data-testid="windows-price">
          <div className="flex items-end gap-2 flex-wrap">
            {discounted ? (
              <span className="text-5xl md:text-[44px] font-extrabold leading-none tabular-nums bg-gradient-to-b from-orange-300 to-orange-500 bg-clip-text text-transparent">{finalPrice}</span>
            ) : (
              <span className="text-5xl md:text-[44px] font-extrabold leading-none tabular-nums bg-gradient-to-b from-white to-[#9fb0c0] bg-clip-text text-transparent">{windowsStandardBase}</span>
            )}
            <span className="text-lg font-semibold text-gray-500 mb-1">€</span>
            {discounted && <span className="text-base md:text-lg text-gray-500 line-through mb-1">{windowsStandardBase}€</span>}
          </div>
        </div>

        {discounted && (
          <div className="relative -mt-1 mb-4 flex flex-wrap items-center gap-2">
            <div data-testid="windows-savings" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] md:text-xs font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/40 shadow-[0_0_18px_-6px_rgba(16,185,129,0.7)]">
              💸 {t('services.cards.savings')} {(windowsStandardBase - finalPrice).toFixed(2).replace('.', ',')}€
            </div>
            {remaining !== null && remaining > 0 && (
              <div data-testid="windows-flash-timer" className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] md:text-xs font-bold border tabular-nums ${lastHour ? 'text-red-300 bg-red-500/15 border-red-500/50 animate-pulse' : 'text-[#1de9d0] bg-[rgba(29,233,208,0.08)] border-[rgba(29,233,208,0.3)]'}`}>
                <Clock className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />{t('services.cards.endsIn')} {fmtTimer(remaining)}
              </div>
            )}
          </div>
        )}

        <p className="relative text-xs md:text-sm text-gray-400 leading-relaxed mb-4">
          {t('services.cards.windowsTagline')}
        </p>

        {/* Tag cyan */}
        <div className="relative mb-5" data-testid="windows-delivery">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] md:text-xs font-semibold text-[#1de9d0] bg-[rgba(29,233,208,0.07)] border border-[rgba(29,233,208,0.22)]">
            <TrendingUp className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} />{t('services.cards.instantDelivery')}
          </div>
        </div>

        {/* Features avec pastilles cyan */}
        <div className="relative flex flex-col gap-3 mb-6 flex-1">
          {windowsFeatures.map((feature, i) => (
            <div key={i} data-testid={`windows-feature-${i}`} className="service-card-feature flex items-center gap-3">
              <span className="w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 bg-[rgba(29,233,208,0.08)] border border-[rgba(29,233,208,0.2)]">
                <Check className="w-2.5 h-2.5 text-[#1de9d0]" strokeWidth={3} />
              </span>
              <p className="text-[13px] md:text-sm text-gray-300 leading-snug">{feature}</p>
            </div>
          ))}
        </div>

        {/* Total — passe en vert quand une promo est active */}
        <div data-testid="windows-total" className={`relative mb-4 rounded-xl border p-3 flex items-center justify-between transition-colors ${discounted ? 'border-emerald-500/50 bg-emerald-500/[0.08] shadow-[0_0_22px_-6px_rgba(16,185,129,0.7)]' : 'border-[rgba(29,233,208,0.15)] bg-[rgba(29,233,208,0.03)]'}`}>
          <span className={`text-[11px] md:text-xs font-medium uppercase tracking-wider ${discounted ? 'text-emerald-300' : 'text-gray-400'}`}>{t('services.total')}</span>
          <div className="flex items-baseline gap-1.5">
            {discounted && <span className="text-sm text-gray-600 line-through">{windowsStandardBase}€</span>}
            <span className={`text-2xl font-extrabold tabular-nums ${discounted ? 'text-emerald-400' : 'text-white'}`}>{finalPrice}</span>
            <span className={`text-base font-bold ${discounted ? 'text-emerald-500' : 'text-gray-500'}`}>€</span>
          </div>
        </div>

        {/* Bouton */}
        <Button onClick={() => openOrderModal('windows')} disabled={loading === 'windows'} data-testid="order-windows-btn"
          className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 hover:-translate-y-0.5 ${discounted ? 'promo-btn-glow' : ''} ${isAuthenticated
            ? 'bg-gradient-to-r from-[#14b8c8] to-[#1de9d0] text-[#06181a] shadow-[0_0_25px_-8px_rgba(29,233,208,0.6)] hover:shadow-[0_0_35px_-6px_rgba(29,233,208,0.8)]'
            : 'bg-[#5865F2] hover:bg-[#4752C4] text-white'}`}>
          {loading === 'windows' ? <span className="flex items-center justify-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{t('services.loading')}</span>
            : success === 'windows' ? t('services.ticketCreated') : isAuthenticated ? <>{t('services.orderButton')} — {finalPrice}€<ChevronRight className="w-4 h-4" strokeWidth={2.5} /></>
            : <span className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" /></svg>
              {t('services.loginToOrder')}</span>}
        </Button>
      </div>
    </ServiceCard>
  );
};

export default WindowsCard;
